import dayjs from 'dayjs';
import { Box, Input, VStack } from '@chakra-ui/react';
import { DatePicker } from '@/components/DatePicker';

interface Props {
  value?: any;
  onChange?: (date: any) => void;
}
const RangeDate = (props: Props) => {
  const { t } = useTranslation();
  const { value, onChange } = props;
  const [startDate, setStartDate] = useState<Date | null>(value?.[0]?.toDate?.() || null);
  const [endDate, setEndDate] = useState<Date | null>(value?.[1]?.toDate?.() || null);

  const maxDate = new Date();

  const handleStartDateChange = useCallback(
    (date: Date | null) => {
      setStartDate(date);
      if (date && endDate && date > endDate) {
        setEndDate(null);
      }

      if (onChange) {
        const newValue = [
          date ? date : null,
          date && endDate && date <= endDate ? endDate : null,
        ];
        onChange(newValue);
      }
    },
    [endDate, onChange]
  );

  const handleEndDateChange = useCallback(
    (date: Date | null) => {
      setEndDate(date);
      if (date && startDate && date < startDate) {
        setStartDate(null);
      }
      if (onChange) {
        const newValue = [
          date && startDate && startDate <= date ? startDate : null,
          date ? date : null,
        ];
        onChange(newValue);
      }
    },
    [startDate, onChange]
  );

  const getMinEndDate = useCallback(() => {
    if (!startDate) return undefined;
    return startDate;
  }, [startDate]);

  const getMaxStartDate = useCallback(() => {
    if (!endDate) return maxDate;
    return endDate > maxDate ? maxDate : endDate;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endDate]);
  useEffect(() => {
    if (value) {
      setStartDate((value?.[0] && dayjs(value?.[0])?.toDate?.()) || null);
      setEndDate((value?.[1] && dayjs(value?.[1])?.toDate?.()) || null);
    }
  }, [value]);

  return (
    <VStack
      h="100px"
      w="full"
      css={{
        '& .react-datepicker-wrapper': {
          width: '100%',
        },
      }}
      gap="5"
    >
      <Box w="full">
        <DatePicker
          selected={startDate}
          startDate={startDate}
          endDate={endDate}
          onChange={handleStartDateChange}
          selectsStart
          dateFormat="yyyy-MM-dd HH:mm"
          placeholderText={t('filter.startTime')}
          isClearable
          autoComplete="off"
          showTimeSelect
          timeFormat="HH:mm"
          timeIntervals={15}
          minDate={new Date(0)}
          maxDate={getMaxStartDate()}
          customInput={<Input w="full" />}
          filterDate={(date: any) => date <= maxDate}
          filterTime={(time: any) => {
            const timeDate = new Date(time);
            return timeDate <= maxDate;
          }}
          popperProps={{
            placement: 'bottom-start',
            strategy: 'fixed',
          }}
        />
      </Box>
      <Box w="full">
        <DatePicker
          selected={endDate}
          startDate={startDate}
          endDate={endDate}
          isClearable
          autoComplete="off"
          showTimeSelect
          timeFormat="HH:mm"
          timeIntervals={15}
          onChange={handleEndDateChange}
          selectsEnd
          dateFormat="yyyy-MM-dd HH:mm"
          placeholderText={t('filter.endTime')}
          minDate={getMinEndDate()}
          maxDate={maxDate}
          customInput={<Input size="sm" />}
          filterDate={(date: any) => date <= maxDate}
          filterTime={(time: any) => {
            const timeDate = new Date(time);
            return timeDate <= maxDate;
          }}
          popperProps={{
            placement: 'bottom-start',
            strategy: 'fixed',
          }}
        />
      </Box>
    </VStack>
  );
};
export default RangeDate;
